// Browser → Cloudflare R2 upload helper.
//
// R2 is the sole storage backend. Public buckets are served via
// `https://cdn.podgo.io/<bucket>/<path>`; private buckets use signed S3 GETs
// minted by `get-raw-audio-url` / `sign-private-batch`.

import { supabase } from "@/integrations/supabase/client";
import { cdnUrl } from "@/lib/cdnUrl";
import { logUploadFailure } from "@/services/uploadFailureLog";


export type R2Bucket =
  | "studio-photos"
  | "bug-screenshots"
  | "support-attachments"
  | "show-covers"
  | "avatars"
  | "featured-logos"
  | "blog-images"
  | "episode-media"
  | "episode-media-raw"
  | "episode-drafts"
  | "resumes"
  | "voice-samples"
  | "company-assets";

export interface R2UploadParams {
  bucket: R2Bucket;
  path: string;
  file: Blob;
  contentType?: string;
  /** Progress 0..100. When provided, upload runs through XHR. */
  onProgress?: (percent: number) => void;
  /** Abort the upload. When provided, upload runs through XHR. */
  signal?: AbortSignal;
}

export interface R2UploadResult {
  publicUrl: string;
  path: string;
  bucket: string;
}

interface SignResponse {
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  publicUrl: string;
  bucket: string;
  path: string;
  expiresAt: number;
  /** Set when the object should be stamped with long-lived cache headers. */
  cacheControl?: string | null;
}

/** Files at or above this size upload part-by-part. */
const MULTIPART_THRESHOLD_BYTES = 50 * 1024 * 1024;
/**
 * R2 requires every part except the last to be >= 5MB. 8MB keeps the cost of a
 * dropped connection small: failure logs show parts dying 0.5-3s in, so a
 * smaller part means less to redo.
 */
const PART_SIZE_BYTES = 8 * 1024 * 1024;
/** Attempts per PUT (initial + retries) before giving up. */
const MAX_ATTEMPTS = 6;
/** Longest wait between attempts. */
const MAX_BACKOFF_MS = 15000;

/** Exponential backoff with jitter — bursts of instant retries help nothing. */
function backoffMs(attempt: number): number {
  const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** (attempt - 1));
  return Math.round(base * (0.75 + Math.random() * 0.5));
}

type SignBody = Record<string, unknown>;

async function invokeSign(body: SignBody) {
  // Force any pending token refresh to complete before invoke()'s internal
  // fetch reads the session. Long uploads can outlive the access token, and a
  // silently expired JWT surfaces as a FunctionsFetchError, not a 401.
  try {
    await supabase.auth.getSession();
  } catch {
    /* non-fatal: invoke will surface the real error */
  }
  return supabase.functions.invoke("storage-sign-upload", { body });
}

async function callSign<T>(body: SignBody): Promise<T> {
  let { data, error } = await invokeSign(body);

  // Retry once on FunctionsFetchError (underlying fetch rejected — network
  // hiccup, token refresh race, backgrounded tab). Do NOT retry on HTTP
  // errors (FunctionsHttpError) — those are deterministic.
  if (error && (error as { name?: string }).name === "FunctionsFetchError") {
    await new Promise((r) => setTimeout(r, 750));
    ({ data, error } = await invokeSign(body));
  }

  if (error) {
    const name = (error as { name?: string }).name || "Error";
    const message = (error as { message?: string }).message || String(error);
    throw new Error(`R2 sign failed (${name}): ${message}`);
  }
  return data as T;
}

async function signUpload(
  bucket: R2Bucket,
  path: string,
  contentType: string,
): Promise<SignResponse> {
  const signed = await callSign<SignResponse>({ bucket, path, contentType });
  if (!signed?.uploadUrl) throw new Error("R2 sign response missing uploadUrl");
  return signed;
}

/** Network-level failures are worth retrying; HTTP 4xx from R2 is not. */
class UploadNetworkError extends Error {
  loadedBytes?: number;
}

/**
 * Every retry of a dropped transfer is exhausted. The multipart session (when
 * there is one) is deliberately left open so the next call resumes from the
 * last accepted chunk instead of restarting the whole file.
 */
export class UploadInterruptedError extends Error {
  name = "UploadInterruptedError";
  /** Chunks already accepted by R2, when the upload was multipart. */
  completedChunks?: number;
  totalChunks?: number;
}

/**
 * Open multipart sessions, keyed by bucket + file identity rather than path:
 * callers mint a new timestamped path on every attempt, so keying on path
 * would make a retry start from zero. Sessions are in-memory only; a page
 * reload starts fresh (the orphan is cleaned up by R2's lifecycle rules).
 */
interface MultipartSession {
  uploadId: string;
  /** The path the session was created against — must be reused verbatim. */
  path: string;
  parts: { partNumber: number; etag: string }[];
  completedBytes: number;
  startedAt: number;
}

const SESSION_TTL_MS = 6 * 60 * 60 * 1000; // matches the 24h signed-URL headroom, conservatively
const multipartSessions = new Map<string, MultipartSession>();

function sessionKey(bucket: string, file: Blob): string {
  const f = file as File;
  return [bucket, file.size, f.name ?? "", f.lastModified ?? 0].join("|");
}

function takeSession(key: string): MultipartSession | null {
  const existing = multipartSessions.get(key);
  if (!existing) return null;
  if (Date.now() - existing.startedAt > SESSION_TTL_MS) {
    multipartSessions.delete(key);
    return null;
  }
  return existing;
}

/** HTTP-level failure from R2 — carries the status for the failure log. */
class UploadHttpError extends Error {
  constructor(
    message: string,
    readonly httpStatus: number,
    readonly loadedBytes?: number,
  ) {
    super(message);
  }
}

function errorDetails(err: unknown): { httpStatus?: number; loadedBytes?: number } {
  if (err instanceof UploadHttpError) {
    return { httpStatus: err.httpStatus, loadedBytes: err.loadedBytes };
  }
  if (err instanceof UploadNetworkError) return { loadedBytes: err.loadedBytes };
  return {};
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function putWithXhr(
  url: string,
  contentType: string,
  body: Blob,
  onProgress?: (loadedBytes: number) => void,
  signal?: AbortSignal,
): Promise<{ etag: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // Tracked so a failure log can say how far the transfer got before dying.
    let loadedBytes = 0;
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException("Upload cancelled", "AbortError"));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        loadedBytes = e.loaded;
        onProgress?.(e.loaded);
      }
    };
    const networkError = (message: string) => {
      const err = new UploadNetworkError(message);
      err.loadedBytes = loadedBytes;
      return err;
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") });
      } else {
        reject(
          new UploadHttpError(
            `R2 upload failed (${xhr.status}): ${xhr.responseText}`,
            xhr.status,
            loadedBytes,
          ),
        );
      }
    };
    xhr.onerror = () => reject(networkError("Network error during R2 upload"));
    xhr.ontimeout = () => reject(networkError("Timed out during R2 upload"));
    xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(body);
  });
}


/** Shared identity of the object being uploaded, for the failure log. */
interface UploadLogContext {
  bucket: string;
  path: string;
  fileSizeBytes?: number;
  contentType?: string;
  mode: "single" | "multipart";
  partNumber?: number;
}

/**
 * Run one PUT with retries. Each attempt re-signs, because a stale or
 * mid-flight-expired signed URL looks identical to a dropped connection.
 * Every failed attempt is logged so drops can be investigated after the fact.
 */
async function putWithRetries(
  sign: () => Promise<string>,
  contentType: string,
  body: Blob,
  onProgress?: (loadedBytes: number) => void,
  signal?: AbortSignal,
  label = "upload",
  logContext?: UploadLogContext,
): Promise<{ etag: string | null }> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const startedAt = Date.now();
    try {
      const url = await sign();
      return await putWithXhr(url, contentType, body, onProgress, signal);
    } catch (err) {
      if (isAbort(err)) throw err;
      lastError = err;
      const retryable = err instanceof UploadNetworkError;
      const isFinal = !retryable || attempt === MAX_ATTEMPTS;
      if (logContext) {
        logUploadFailure({
          ...logContext,
          attempt,
          isFinal,
          errorMessage: err instanceof Error ? err.message : String(err),
          elapsedMs: Date.now() - startedAt,
          ...errorDetails(err),
        });
      }
      if (isFinal) break;
      onProgress?.(0); // the failed attempt's bytes never landed
      await new Promise((r) => setTimeout(r, backoffMs(attempt)));
    }
  }
  if (lastError instanceof UploadNetworkError) {
    throw new UploadInterruptedError(
      `Upload interrupted after ${MAX_ATTEMPTS} attempts (${label}). ` +
        `Check your connection, keep this tab open, and try again.`,
    );
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}


async function uploadMultipart(
  params: R2UploadParams,
  contentType: string,
): Promise<R2UploadResult> {
  const { bucket, file } = params;
  const key = sessionKey(bucket, file);
  const total = file.size;
  const totalChunks = Math.max(1, Math.ceil(total / PART_SIZE_BYTES));

  // Resume an interrupted session for the same file instead of re-uploading
  // chunks R2 has already accepted.
  let session = takeSession(key);
  if (!session) {
    const created = await callSign<{ uploadId: string; publicUrl: string }>({
      bucket,
      path: params.path,
      contentType,
      op: "multipart_create",
    });
    if (!created?.uploadId) throw new Error("R2 multipart create returned no uploadId");
    session = {
      uploadId: created.uploadId,
      path: params.path,
      parts: [],
      completedBytes: 0,
      startedAt: Date.now(),
    };
    multipartSessions.set(key, session);
  }

  const { uploadId, path } = session;
  const parts = session.parts;
  let completedBytes = session.completedBytes;
  const firstPartNumber = parts.length + 1;

  try {
    for (
      let partNumber = firstPartNumber, offset = (firstPartNumber - 1) * PART_SIZE_BYTES;
      offset < total;
      offset += PART_SIZE_BYTES, partNumber++
    ) {
      const chunk = file.slice(offset, Math.min(offset + PART_SIZE_BYTES, total));
      const baseBytes = completedBytes;
      const { etag } = await putWithRetries(
        async () => {
          const signed = await callSign<{ uploadUrl: string }>({
            bucket,
            path,
            op: "multipart_sign_part",
            uploadId,
            partNumber,
          });
          if (!signed?.uploadUrl) throw new Error("R2 part sign returned no uploadUrl");
          return signed.uploadUrl;
        },
        contentType,
        chunk,
        params.onProgress
          ? (loaded) =>
              params.onProgress?.(
                Math.min(99, Math.round(((baseBytes + loaded) / total) * 100)),
              )
          : undefined,
        params.signal,
        `chunk ${partNumber} of ${totalChunks}`,
        {
          bucket,
          path,
          fileSizeBytes: file.size,
          contentType,
          mode: "multipart",
          partNumber,
        },
      );

      if (!etag) {
        // R2 CORS must expose ETag for multipart completion to be possible.
        throw new Error("R2 part response did not expose an ETag header");
      }
      parts.push({ partNumber, etag });
      completedBytes += chunk.size;
      // Persist progress so a later retry picks up here.
      session.parts = parts;
      session.completedBytes = completedBytes;
    }

    const done = await callSign<{ publicUrl: string; path: string; bucket: string }>({
      bucket,
      path,
      op: "multipart_complete",
      uploadId,
      parts,
    });
    multipartSessions.delete(key);
    params.onProgress?.(100);
    return {
      publicUrl: done?.publicUrl ?? r2PublicUrl(bucket as R2Bucket, path),
      path,
      bucket,
    };
  } catch (err) {
    // A dropped connection keeps the session alive so the next attempt resumes;
    // anything else (cancel, bad config, failed completion) tears it down.
    if (err instanceof UploadInterruptedError) {
      err.completedChunks = parts.length;
      err.totalChunks = totalChunks;
      err.message =
        `Upload interrupted — ${parts.length} of ${totalChunks} chunks uploaded. ` +
        `Your progress is saved; check your connection, keep this tab open, and resume.`;
      throw err;
    }
    multipartSessions.delete(key);
    await callSign({ bucket, path, op: "multipart_abort", uploadId }).catch(() => null);
    throw err;
  }
}

export async function uploadToR2(params: R2UploadParams): Promise<R2UploadResult> {
  const contentType =
    params.contentType ||
    (params.file as File).type ||
    "application/octet-stream";

  // Large files upload part-by-part so a dropped connection costs one part,
  // not the whole episode.
  if (params.file.size >= MULTIPART_THRESHOLD_BYTES) {
    try {
      return await uploadMultipart(params, contentType);
    } catch (err) {
      // Multipart needs ETag exposed via CORS; fall back to a single PUT if the
      // bucket config doesn't allow it rather than blocking the upload.
      if (isAbort(err) || !(err instanceof Error) || !err.message.includes("ETag")) throw err;
      console.warn("[r2Upload] multipart unavailable, falling back to single PUT:", err.message);
    }
  }

  const total = params.file.size || 1;
  let lastSigned: SignResponse | null = null;
  await putWithRetries(
    async () => {
      lastSigned = await signUpload(params.bucket, params.path, contentType);
      return lastSigned.uploadUrl;
    },
    contentType,
    params.file,
    params.onProgress
      ? (loaded) => params.onProgress?.(Math.round((loaded / total) * 100))
      : undefined,
    params.signal,
    "upload",
    {
      bucket: params.bucket,
      path: params.path,
      fileSizeBytes: params.file.size,
      contentType,
      mode: "single",
    },
  );


  const signed = lastSigned as SignResponse | null;

  // Public image objects are cached for a year at the CDN. R2 stores that
  // header, and the browser cannot set it on the signed PUT (it would need a
  // CORS preflight allowance), so the backend stamps it right after the upload.
  // Non-fatal: the file is already stored, and the backfill catches misses.
  if (signed?.cacheControl) {
    callSign({
      bucket: params.bucket,
      path: params.path,
      contentType,
      op: "set_cache_control",
    }).catch((err) =>
      console.warn("[r2Upload] cache-control not applied:", (err as Error).message),
    );
  }

  return {
    publicUrl: signed?.publicUrl ?? r2PublicUrl(params.bucket, params.path),
    path: signed?.path ?? params.path,
    bucket: signed?.bucket ?? params.bucket,
  };
}



/**
 * Build the public CDN URL for an R2 object without an upload round-trip.
 * Used for read-only paths that previously called `supabase.storage.getPublicUrl()`.
 */
export function r2PublicUrl(bucket: R2Bucket, path: string): string {
  return cdnUrl(bucket, path);
}
